import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";
import Sparkles from "@/components/Sparkles";
import { adminTokenStore } from "@/services/sellerTokenStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// axios.defaults isn't a good fit here because it's global; make a local
// instance that always includes cookies so the httpOnly refresh cookie set
// by /auth/admin/login rides along on every subsequent admin API call.
axios.defaults.withCredentials = true;

/**
 * Score a password 0-4 and return {score, label, color, checks}.
 * 0 = empty, 1 = weak, 2 = fair, 3 = good, 4 = strong.
 */
const scorePassword = (pw) => {
  const checks = {
    length: pw.length >= 8,
    lengthStrong: pw.length >= 12,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
  if (!pw) return { score: 0, label: "", color: "bg-gray-200", textColor: "text-gray-400", checks };

  let score = 0;
  if (checks.length) score += 1;
  if (checks.lower && checks.upper) score += 1;
  if (checks.digit) score += 1;
  if (checks.symbol) score += 1;
  if (checks.lengthStrong && score >= 3) score = 4;
  if (pw.length < 6) score = 1;
  score = Math.min(4, Math.max(1, score));

  const meta = {
    1: { label: "Weak", color: "bg-red-500", textColor: "text-red-600" },
    2: { label: "Fair", color: "bg-orange-500", textColor: "text-orange-600" },
    3: { label: "Good", color: "bg-yellow-500", textColor: "text-yellow-600" },
    4: { label: "Strong", color: "bg-emerald-500", textColor: "text-emerald-600" },
  }[score];
  return { score, ...meta, checks };
};

const PasswordStrengthMeter = ({ password }) => {
  const { score, label, color, textColor, checks } = scorePassword(password);
  if (!password) return null;
  const segments = [1, 2, 3, 4];
  return (
    <div className="mt-2" data-testid="password-strength-meter">
      <div className="flex gap-1.5 mb-1.5">
        {segments.map((n) => (
          <div
            key={n}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              n <= score ? color : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <span
          className={`text-xs font-semibold ${textColor}`}
          data-testid="password-strength-label"
        >
          {label}
        </span>
        <span className="text-[11px] text-gray-500">
          {checks.length ? "✓" : "•"} 8+ chars{"  "}
          {checks.upper && checks.lower ? "✓" : "•"} Aa{"  "}
          {checks.digit ? "✓" : "•"} 0-9{"  "}
          {checks.symbol ? "✓" : "•"} !@#
        </span>
      </div>
    </div>
  );
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [username, setUsername] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [formData, setFormData] = useState({ username: "", password: "" });

  // Forgot password flow
  const [showReset, setShowReset] = useState(false);
  const [resetStep, setResetStep] = useState("email"); // "email" | "code"
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const closeReset = () => {
    setShowReset(false);
    setResetStep("email");
    setResetEmail("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    try {
      setResetLoading(true);
      await axios.post(`${API}/auth/admin/forgot-password`, {
        email: resetEmail.trim(),
      });
      setResetStep("code");
      toast.success("Reset code sent to your email!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset code");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetCode.length !== 6) {
      toast.error("Please enter the 6-digit code");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (scorePassword(newPassword).score < 2) {
      toast.error("Password is too weak — add uppercase, numbers, or symbols");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      setResetLoading(true);
      await axios.post(`${API}/auth/admin/reset-password`, {
        email: resetEmail.trim(),
        code: resetCode,
        new_password: newPassword,
      });
      toast.success("Password reset! You can now log in.");
      closeReset();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed");
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/admin/login`, formData, { withCredentials: true });
      if (response.data.requires_2fa) {
        setUsername(response.data.username);
        setMaskedEmail(response.data.email);
        setShowVerification(true);
        toast.success("Verification code sent to your email!");
      } else {
        // Phase-5 hardening: JWT lives in memory + httpOnly cookie only.
        // The `admin_user` blob (name/email/role) is fine in localStorage —
        // it's not a credential and the dashboard uses it for UX.
        adminTokenStore.setSession(response.data.access_token, response.data.user);
        try { localStorage.setItem("admin_user", JSON.stringify(response.data.user)); } catch (_) { /* private mode */ }
        toast.success("Login successful! (2FA disabled)");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/auth/admin/verify-2fa?username=${username}&code=${verificationCode}`,
        {},
        { withCredentials: true }
      );
      adminTokenStore.setSession(response.data.access_token, response.data.user);
      try { localStorage.setItem("admin_user", JSON.stringify(response.data.user)); } catch (_) { /* private mode */ }
      toast.success("Login successful!");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowVerification(false);
    setVerificationCode("");
    setFormData({ username: "", password: "" });
  };

  // ------------------------------------------------------------------
  // Full-screen Forgot Password flow (matches the requested mockup)
  // ------------------------------------------------------------------
  if (showReset) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
        data-testid="reset-password-screen"
        style={{
          background:
            "radial-gradient(ellipse at top, #1e6fd9 0%, #0b52b3 40%, #0a4599 100%)",
        }}
      >
        {/* Consistent app-wide sparkle background */}
        <Sparkles count={30} color="rgba(255,255,255,0.85)" minSize={6} maxSize={16} />

        {/* Card */}
        <div
          className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl px-10 py-10"
          data-testid="reset-password-card"
        >
          <button
            type="button"
            data-testid="reset-back-to-login-btn"
            onClick={closeReset}
            className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>

          {/* Envelope icon */}
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center">
              <Mail className="w-7 h-7 text-blue-600" strokeWidth={2} />
            </div>
          </div>

          {resetStep === "email" ? (
            <form onSubmit={handleSendResetCode} className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Reset your password
                </h2>
                <p className="text-sm text-gray-500">
                  Enter the email address you registered with
                </p>
              </div>

              <div>
                <label
                  htmlFor="reset-email"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  Email Address
                </label>
                <input
                  id="reset-email"
                  data-testid="reset-email-input"
                  type="email"
                  autoFocus
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 text-gray-900 placeholder-gray-400"
                />
              </div>

              <button
                type="submit"
                data-testid="send-reset-code-btn"
                disabled={resetLoading}
                className="w-full py-3.5 rounded-lg text-white font-semibold text-sm shadow-lg disabled:opacity-60 transition-all"
                style={{
                  background:
                    "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                }}
              >
                {resetLoading ? "Sending..." : "Send Reset Code"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Enter reset code
                </h2>
                <p className="text-sm text-gray-500">
                  Check your email for the 6-digit code
                </p>
              </div>

              <div>
                <label
                  htmlFor="reset-code"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  6-Digit Code
                </label>
                <input
                  id="reset-code"
                  data-testid="reset-code-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  placeholder="Enter 6-digit code"
                  value={resetCode}
                  onChange={(e) =>
                    setResetCode(e.target.value.replace(/\D/g, ""))
                  }
                  className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-white text-center text-lg font-mono tracking-[0.4em] text-gray-900 placeholder-gray-400"
                />
              </div>

              <div>
                <label
                  htmlFor="new-password"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  New Password
                </label>
                <input
                  id="new-password"
                  data-testid="new-password-input"
                  type="password"
                  placeholder="Minimum 8 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-white text-gray-900 placeholder-gray-400"
                />
                <PasswordStrengthMeter password={newPassword} />
              </div>

              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-semibold text-gray-900 mb-2"
                >
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  data-testid="confirm-password-input"
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-white text-gray-900 placeholder-gray-400"
                />
              </div>

              <button
                type="submit"
                data-testid="confirm-reset-btn"
                disabled={resetLoading}
                className="w-full py-3.5 rounded-lg text-white font-semibold text-sm shadow-lg disabled:opacity-60 transition-all"
                style={{
                  background:
                    "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                }}
              >
                {resetLoading ? "Resetting..." : "Reset Password"}
              </button>

              <button
                type="button"
                data-testid="resend-code-btn"
                onClick={handleSendResetCode}
                disabled={resetLoading}
                className="w-full py-3 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Didn't receive the code? Send again
              </button>
            </form>
          )}

          <div className="pt-8 mt-8 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              © 2026 Maidly Business Manager
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------------
  // Standard Login / 2FA view — matching blue starfield design
  // ------------------------------------------------------------------
  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      data-testid="admin-login-screen"
      style={{
        background:
          "radial-gradient(ellipse at top, #1e6fd9 0%, #0b52b3 40%, #0a4599 100%)",
      }}
    >
      {/* Consistent app-wide sparkle background */}
      <Sparkles count={30} color="rgba(255,255,255,0.85)" minSize={6} maxSize={16} />

      {/* Top bar */}
      <div className="relative z-10 py-5 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Maidly Business</h1>
          <button
            data-testid="back-to-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </div>

      {/* Card */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6">
        <div
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl px-10 py-10"
          data-testid="admin-login-card"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-8">
            {showVerification
              ? "Two-Step Verification"
              : "Sign in to your account"}
          </h2>

            {!showVerification ? (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Username
                  </label>
                  <input
                    id="username"
                    data-testid="admin-username-input"
                    type="text"
                    placeholder="Enter username"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-colors"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    data-testid="admin-password-input"
                    type="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-white text-gray-900 placeholder-gray-400 transition-colors"
                  />
                </div>

                <button
                  data-testid="admin-login-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-lg text-white font-semibold text-sm shadow-lg disabled:opacity-60 transition-all"
                  style={{
                    background:
                      "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                  }}
                >
                  {loading ? "Signing in..." : "Log in"}
                </button>

                <div className="pt-1 text-center">
                  <button
                    type="button"
                    data-testid="forgot-password-btn"
                    onClick={() => setShowReset(true)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerify2FA} className="space-y-5">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                  <p className="font-semibold mb-2">Verification code sent!</p>
                  <p>
                    Check your email: <strong>{maskedEmail}</strong>
                  </p>
                  <p className="text-xs mt-2 text-green-600">
                    Valid for 10 minutes
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="verification-code"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    6-Digit Verification Code
                  </label>
                  <input
                    id="verification-code"
                    data-testid="verification-code-input"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(e.target.value.replace(/\D/g, ""))
                    }
                    className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-lg focus:outline-none focus:border-blue-500 focus:bg-white text-center text-2xl font-mono tracking-widest"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="flex-1 py-3 border border-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    data-testid="verify-2fa-btn"
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    className="flex-1 py-3 rounded-lg text-white font-semibold shadow-lg disabled:opacity-50 transition-all"
                    style={{
                      background:
                        "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                    }}
                  >
                    {loading ? "Verifying..." : "Verify"}
                  </button>
                </div>
              </form>
            )}

          <div className="pt-8 mt-8 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              © 2026 Maidly Business Manager
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
